import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { PageQuery, paginated } from '../pagination.ts';
import { timestamp } from '../schemas.ts';

// User administration (design §6.1, §6.11). Banning and revoking sessions need a recent login.

const RecordId = z.string().regex(/^[a-z0-9]{15}$/);

export const UserStatus = z.enum(['active', 'banned', 'left']);

export const UserSummary = z.object({
  id: z.string(),
  discordId: z.string(),
  username: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  status: UserStatus,
  lastLoginAt: timestamp.nullable(),
  /** Made by `pnpm seed` for local work; never in a real guild. */
  seeded: z.boolean(),
});
export type UserSummary = z.infer<typeof UserSummary>;

/** How a user reaches a group: a manual membership, or holding a linked Discord role. */
export const GroupReach = z.object({
  groupId: z.string(),
  groupName: z.string(),
  via: z.enum(['manual', 'role']),
  roleId: z.string().nullable(),
  expiresAt: timestamp.nullable(),
});
export type GroupReach = z.infer<typeof GroupReach>;

export const SessionInfo = z.object({
  id: z.string(),
  state: z.enum(['valid', 'revoked', 'idle', 'expired']),
  userAgent: z.string(),
  created: timestamp,
  lastSeenAt: timestamp,
  authenticatedAt: timestamp,
  expiresAt: timestamp,
  revokedAt: timestamp.nullable(),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

export const UserProfile = z.object({
  user: UserSummary,
  /** Guild role ids, kept current by the worker and the reconcile. */
  discordRoles: z.array(z.string()),
  /** In Discord's membership screening, which counts as lost access. */
  pending: z.boolean(),
  /** When Discord last changed their roles, name or avatar; null if never since they were created at login. */
  rolesSyncedAt: timestamp.nullable(),
  groups: z.array(GroupReach),
  /** Effective permissions; ['*'] means all. */
  permissions: z.array(z.string()),
  /** True when ITC_SUPERADMINS names this user. */
  superadmin: z.boolean(),
  sessions: z.array(SessionInfo),
});
export type UserProfile = z.infer<typeof UserProfile>;

const UserParams = z.object({ id: RecordId });

export const searchUsers = defineEndpoint({
  method: 'GET',
  path: '/admin/users',
  permission: 'core.admin.users',
  /** `q` matches part of a username or display name, or a whole Discord id. */
  query: PageQuery.extend({ q: z.string().trim().max(64).optional() }),
  response: paginated(UserSummary),
});

export const getUser = defineEndpoint({
  method: 'GET',
  path: '/admin/users/:id',
  permission: 'core.admin.users',
  params: UserParams,
  response: UserProfile,
});

export const revokeUserSessions = defineEndpoint({
  method: 'POST',
  path: '/admin/users/:id/sessions/revoke',
  permission: 'core.admin.users',
  recentLogin: true,
  params: UserParams,
  /** One session, or every open one when omitted. */
  body: z.object({ sessionId: RecordId.optional() }),
  response: z.object({ revoked: z.number().int() }),
});

/** Bans the user from ITC Web (not from Discord): every session ends, and login is refused. */
export const banUser = defineEndpoint({
  method: 'POST',
  path: '/admin/users/:id/ban',
  permission: 'core.admin.users',
  recentLogin: true,
  params: UserParams,
  response: z.object({ user: UserSummary, revokedSessions: z.number().int() }),
});

export const unbanUser = defineEndpoint({
  method: 'POST',
  path: '/admin/users/:id/unban',
  permission: 'core.admin.users',
  recentLogin: true,
  params: UserParams,
  response: z.object({ user: UserSummary }),
});
